import { Options, Vue } from 'vue-class-component';
import { mapState } from 'vuex';
import {
  EntrancePlantDetailDTO,
  EPDeviceTypeEnum,
  GateMessageDTO,
} from '@/../client-generator/generated-clients/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';
import plantsClient from '@/core/services/plants';
import { PropType } from 'vue';
import HourPicker from '@/views/components/HourPicker/HourPicker.vue';

@Options({
  name: 'DialogAddEditMessage',
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    messageToEdit: {
      type: Object as PropType<GateMessageDTO>,
      required: false,
    },
  },
  computed: {
    ...mapState(['currentEntrancePlant']),
  },
  watch: {
    isVisible: function (newValue) {
      this.isDialogVisible = newValue;
      if (this.isDialogVisible) {
        this.initMessage();
      }
    },
    'dateSince': function () {
      this.checkIfValidTime();
      this.checkIfAnotherMessageExists();
    },
    'dateUntil': function () {
      this.checkIfValidTime();
      this.checkIfAnotherMessageExists();
    },
    'message.gateIds': {
      deep: true,
      handler() {
        this.checkIfValidTime();
        this.checkIfAnotherMessageExists();
      }
    },
  },
  components: {HourPicker},
  emits: ['hide-dialog', 'update-list'],
})
export default class DialogAddEditMessage extends Vue {
  messageToEdit!: GateMessageDTO;
  currentEntrancePlant!: EntrancePlantDetailDTO;

  message: GateMessageDTO = new GateMessageDTO();
  readonly titleMaxLength = 20;
  readonly messageMaxLength = 225;
  isVisible!: boolean;
  isDialogVisible = false;
  isEdit = false;
  noOverlapping = false;
  validTime = true;

  dateSince = new Date();
  dateUntil = new Date();

  timeSince = 0;
  timeUntil = 0;

  get dialogTitle() {
    return this.isEdit
      ? this.$t('AppResourceEP.EditGateMessage')
      : this.$t('AppResourceEP.NewGateMessage');
  }

  get currentEntrancePlantGatesCount() {
    return this.gates?.length ?? 0;
  }

  get remainingChars() {
    if (!this.message.text) return this.messageMaxLength;
    return this.messageMaxLength - this.message.text.length;
  }

  get gates() {
    return this.currentEntrancePlant.gates?.filter((gate) => gate.devices?.some(
      (device) => device.type === EPDeviceTypeEnum.Linea5000
      ))
  }

  get today() { 
    return new Date();
  }

  get minDate() {
    return this.today;
  }

  get maxDate() {
    const nextYear = new Date(this.minDate);
    nextYear.setUTCFullYear(this.minDate.getUTCFullYear() + 1);
    return nextYear;
  }

  get startDate() {
    const startDate = new Date();
    startDate.setUTCHours(0, 0);
    return startDate;
  }

  get endDate() {
    const nextMonth = new Date(this.today);
    nextMonth.setUTCMonth(this.today.getUTCMonth() + 1);
    nextMonth.setUTCHours(0, 0);
    return nextMonth;
  }

  get minDurationUntilDate() {
    const nextDay = new Date(this.dateSince!);
    return nextDay;
  }

  checkIfAnotherMessageExists() {
    this.noOverlapping = !this.selectedDurationHasOverlappings();
  }

  checkIfValidTime() {
    const today = this.createDate(this.today, this.today.getHours());
    const dateToSinceCheck = this.createDate(this.dateSince, this.timeSince);
    const dateToUntilCheck = this.createDate(this.dateUntil, this.timeUntil);
    this.validTime = dateToUntilCheck > today && dateToSinceCheck < dateToUntilCheck;
  }

  selectedDurationHasOverlappings() {
    const dateToSinceCheck = this.createDate(this.dateSince, this.timeSince);
    const dateToUntilCheck = this.createDate(this.dateUntil, this.timeUntil);
    for (let gateId of this.message.gateIds ?? []) {
      const existingMessagesForGate = this.currentEntrancePlant.gateMessages!.filter(
          (message) =>
            message.gateIds!.includes(gateId) &&
            message.id !== this.message.id
      );
      if (!existingMessagesForGate.every(m => (m.durationSince! >= dateToUntilCheck && m.durationUntil! >= dateToUntilCheck)
          || (m.durationSince! <= dateToSinceCheck && m.durationUntil! <= dateToSinceCheck)))
          return true;
    }
    return false;
  }

  handleHideDialog() {
    this.isDialogVisible = false;
  }

  doHideDialog() {
    this.$emit('hide-dialog');
  }

  initMessage() {
    if (this.messageToEdit) {
      this.isEdit = true;
      this.message = new GateMessageDTO(this.messageToEdit);
    } else {
      this.isEdit = false;
      this.message = new GateMessageDTO({
        title: '',
        text: '',
        durationSince: new Date(this.startDate),
        durationUntil: new Date(this.endDate),
        gateIds: [],
      });
    }
    this.timeSince = this.message.durationSince!.getUTCHours() 
    this.timeUntil = this.message.durationUntil!.getUTCHours()
    // take only date part without being affected by timezone
    this.dateSince = new Date(this.message.durationSince!.toISOString().split('Z')[0]) 
    this.dateUntil = new Date(this.message.durationUntil!.toISOString().split('Z')[0])
  }

  async handleButtonSubmitAction() {
    this.isDialogVisible = false;

    await this.saveGateMessage();

    this.$emit('update-list');
  }

  async saveGateMessage() {
    if (this.currentEntrancePlant.desktopId === undefined) return;
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      this.message.durationSince = this.createDate(this.dateSince, this.timeSince);
      this.message.durationUntil = this.createDate(this.dateUntil, this.timeUntil);
      if(this.message.durationSince!.getTime() > this.message.durationUntil!.getTime()) {
        throw new Error('Invalid time range');
      }

      if (this.isEdit) {
        await plantsClient.gatemessagePUT(
          this.currentEntrancePlant.desktopId,
          this.message
        );
      } else {
        await plantsClient.gatemessagePOST(
          this.currentEntrancePlant.desktopId,
          this.message
        );
      }

      this.$toast.add({
        severity: 'success',
        summary: this.isEdit 
          ? this.$t('entrancePlant.gateMessageEdited') 
          : this.$t('AppResourceEP.GateMessageBannerCreated'),
        closable: false,
        life: 3000,
      });
    } catch (error) {
      console.log(error);
      setTimeout(() => {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          closable: false,
          life: 3000,
        });
      }, 1500);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  createDate(date: Date, hours: number) {
    const newDate = new Date();
    newDate.setUTCFullYear(date.getFullYear());
    newDate.setUTCMonth(date.getMonth());
    newDate.setUTCDate(date.getDate());
    newDate.setUTCHours(hours, 0, 0, 0);
    return newDate;
  }

  get saveButtonDisabled() {
    const badTitle = !this.message.title || this.message.title.trim().length === 0;
    const badText = !this.message.text || this.message.text?.trim().length === 0;
    const badTime = !this.validTime;
    const overlap = !this.noOverlapping;
    const badGates = this.message.gateIds?.length === 0;
    return badTitle || badText || badTime || badGates || overlap;
  }

  updateUntil(value: number) {
    this.timeUntil = value;
    this.checkIfValidTime();
    this.checkIfAnotherMessageExists();
  }

  updateSince(value: number) {
    this.timeSince = value;
    if(this.timeSince > this.timeUntil) {
      this.timeUntil = this.timeSince +1;
    }
    this.checkIfValidTime();
    this.checkIfAnotherMessageExists();
  }
}
